/**
 * TASK PHASE CONFIGURATION
 * 
 * Single source of truth for all task phase-related configurations.
 * This file defines:
 * - Frontend column IDs and their display properties
 * - Mapping between backend TaskPhase enum and frontend columns
 * - Filter configurations
 * - Phase display names and colors
 * 
 * To modify the kanban board structure:
 * 1. Update the taskPhaseColumns array for visual changes
 * 2. Update the mapping functions for phase grouping logic
 * 3. All dependent components will automatically reflect changes
 */

import { TaskPhase } from "shared/types/task";

// Frontend display column ID (used for grouping multiple backend phases)
export type FrontendColumnId = 
  | 'in-te-plannen'
  | 'gepland'
  | 'in-uitvoering'
  | 'te-valideren'
  | 'afgerond';

// Column configuration for the kanban board
export interface TaskPhaseColumn {
  id: FrontendColumnId;
  label: string;
  color: string;
  bgColor: string;
  headerBgColor: string;
  borderColor: string;
}

/**
 * Task phase column configurations for the kanban board
 * Defines the visual appearance and order of columns
 */
export const taskPhaseColumns: TaskPhaseColumn[] = [
  {
    id: 'in-te-plannen',
    label: 'In te plannen',
    color: 'text-neutral-600',
    bgColor: 'bg-neutral-50/50',
    headerBgColor: 'bg-neutral-100',
    borderColor: 'border-neutral-200',
  },
  {
    id: 'gepland',
    label: 'Gepland',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50/50',
    headerBgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  {
    id: 'in-uitvoering',
    label: 'In uitvoering',
    color: 'text-brick-primary',
    bgColor: 'bg-brick-primary/5',
    headerBgColor: 'bg-brick-primary/10',
    borderColor: 'border-brick-primary/20',
  },
  {
    id: 'te-valideren',
    label: 'Te valideren',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50/50',
    headerBgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
  },
  {
    id: 'afgerond',
    label: 'Afgerond',
    color: 'text-green-600',
    bgColor: 'bg-green-50/50',
    headerBgColor: 'bg-green-50',
    borderColor: 'border-green-200',
  },
];

/**
 * Map backend TaskPhase to frontend column ID
 * This determines which column a task appears in based on its phase
 * 
 * Multiple backend phases can map to the same frontend column:
 * - 'gepland' includes: TOMORROW, THIS_WEEK, NEXT_WEEK, LATER
 * - 'te-valideren' includes: REVIEW, REMARKS
 * - 'afgerond' includes: VALIDATED, DONE
 */
export const getColumnForPhase = (phase: TaskPhase): FrontendColumnId => {
  switch (phase) {
    case TaskPhase.INBOX:
      return 'in-te-plannen';
    case TaskPhase.TODAY:
      return 'in-uitvoering';
    case TaskPhase.TOMORROW:
    case TaskPhase.THIS_WEEK:
    case TaskPhase.NEXT_WEEK:
    case TaskPhase.LATER:
      return 'gepland';
    case TaskPhase.REVIEW:
    case TaskPhase.REMARKS:
      return 'te-valideren';
    case TaskPhase.VALIDATED:
    case TaskPhase.DONE:
      return 'afgerond';
    default:
      return 'in-te-plannen';
  }
};

/**
 * Map frontend column ID to default backend TaskPhase
 * This determines which phase to assign when:
 * - Creating a new task from a column
 * - Dropping a task into a column
 *
 * For columns that group multiple phases, we choose the most logical default:
 * - 'gepland' → THIS_WEEK (most common planning horizon)
 * - 'te-valideren' → REVIEW (default validation state)
 * - 'afgerond' → DONE (completed state)
 */
export const getPhaseForColumn = (columnId: FrontendColumnId): TaskPhase => {
  switch (columnId) {
    case 'in-te-plannen':
      return TaskPhase.INBOX;
    case 'gepland':
      return TaskPhase.THIS_WEEK; // Default to THIS_WEEK when dropped into "Gepland"
    case 'in-uitvoering':
      return TaskPhase.TODAY;
    case 'te-valideren':
      return TaskPhase.REVIEW; // Default to REVIEW (can still have REMARKS tasks here)
    case 'afgerond':
      return TaskPhase.DONE;
    default:
      return TaskPhase.INBOX;
  }
};

/**
 * Available phases for the FilterBar component
 * These are the frontend column IDs that users can filter by
 */
export const availablePhases = taskPhaseColumns.map((column: TaskPhaseColumn) => ({
  key: column.id,
  label: column.label,
}));

/**
 * Get column configuration by ID
 */
export const getColumnById = (id: FrontendColumnId): TaskPhaseColumn | undefined => {
  return taskPhaseColumns.find((col: TaskPhaseColumn) => col.id === id);
};

/**
 * Get column configuration for a given task phase
 */
export const getColumnForTaskPhase = (phase: TaskPhase): TaskPhaseColumn | undefined => {
  const columnId = getColumnForPhase(phase);
  return getColumnById(columnId);
};

